import React from 'react';

import Stack from '@mui/material/Stack';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockButton from '../components/GuideMockButton';
import GuideMockContainer from '../components/GuideMockContainer';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideTrainingWorkspace: React.FC = () => {
  return (
    <GuidePageLayout
      title="ワークスペースを操作する"
      description="フォルダ・ファイルの作成/リネーム/削除、ファイルの編集・保存を行います。"
      breadcrumbs={[
        { label: 'ワークスペース（コードエディタ）概要', path: '/guide/training/overview' },
      ]}
    >
      <GuideSection title="ファイルツリーを表示する">
        <GuideStepList
          steps={[
            { label: 'ワークスペース（コードエディタ）画面を開く' },
            { label: 'アクティビティバーで「エクスプローラー」を選択する' },
            {
              label: 'ツリーが表示される（取得中はローディング表示）',
              description: '各階層は「フォルダ先 → ファイル後、名前昇順」で並びます。',
            },
            { label: '上部の「全て開く」「全て閉じる」でフォルダを一括開閉できる' },
          ]}
        />
        <Stack spacing={1} sx={{ mt: 2 }}>
          <GuideInfoBox>新規ユーザーのワークスペースは空の状態から始まります。</GuideInfoBox>
        </Stack>
      </GuideSection>

      <GuideSection title="フォルダ / ファイルを作成する">
        <GuideStepList
          steps={[
            {
              label: '作成先を選ぶ',
              description:
                '選択中がフォルダならその配下、ファイルならその親フォルダ配下、未選択ならルート直下に作成されます。',
            },
            {
              label: 'エクスプローラー上部の作成アイコンを押す',
              content: (
                <GuideMockContainer label="作成アイコン">
                  <Stack direction="row" spacing={1}>
                    <GuideMockButton label="新規フォルダ" variant="outlined" />
                    <GuideMockButton label="新規ファイル" variant="outlined" />
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '名前を入力して「作成」を押す',
              description: 'ファイル名は拡張子込みで入力します（例: Main.java）。',
            },
          ]}
        />
        <Stack spacing={1} sx={{ mt: 2 }}>
          <GuideInfoBox type="warning">
            同じ階層に同名のフォルダ/ファイルがある場合、拡張子が対象外（.java / .md
            以外）の場合は作成できません。
          </GuideInfoBox>
        </Stack>
      </GuideSection>

      <GuideSection title="名前の変更 / 削除">
        <GuideStepList
          steps={[
            { label: 'ツリーのノードを右クリックする' },
            { label: 'コンテキストメニューから「名前の変更」または「削除」を選ぶ' },
            { label: '確認ダイアログで実行する' },
          ]}
        />
        <Stack spacing={1} sx={{ mt: 2 }}>
          <GuideInfoBox type="warning">
            ファイルの拡張子は変更できません。フォルダは中身が空でないと削除できません。ファイルを削除すると内容と実行履歴も削除されます（取り消し不可）。
          </GuideInfoBox>
        </Stack>
      </GuideSection>

      <GuideSection title="ファイルを開いて編集・保存する">
        <GuideStepList
          steps={[
            { label: 'ツリーのファイルをクリックして開く（右ペインにタブが追加される）' },
            { label: 'エディタで内容を編集する（未保存のタブには印が付く）' },
            {
              label: '右下の「保存」ボタンで保存する',
              content: (
                <GuideMockContainer label="保存ボタン">
                  <GuideMockButton label="保存" variant="outlined" />
                </GuideMockContainer>
              ),
            },
          ]}
        />
        <Stack spacing={1} sx={{ mt: 2 }}>
          <GuideInfoBox>
            Markdown
            ファイルは右下のボタンで「編集」と「プレビュー」を切り替えられます。プレビュー中は保存ボタンは表示されません。
          </GuideInfoBox>
          <GuideInfoBox type="warning">
            他のユーザーが同じファイルを先に更新していた場合、保存できず「最新を取得」の案内が表示されます。最新を取得してから編集し直してください。
          </GuideInfoBox>
        </Stack>
      </GuideSection>

      <GuideSection title="ボタンと操作">
        <GuideTable
          headers={['ボタン', '表示条件', '動作']}
          rows={[
            [
              '保存',
              'ファイルを開いている時（md はプレビュー中を除く）',
              '確認なしでファイルを保存する',
            ],
            ['実行', '実行可能拡張子（.java 等）を開いている時', '保存してからコードを実行する'],
            [
              'プレビュー / 編集',
              'md ファイルを開いている時',
              '編集モードとプレビューモードを切り替える',
            ],
          ]}
        />
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideTrainingWorkspace;
